import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { LANGS } from "../i18n/translations";
import { apiErr } from "../lib/apiClient";
import { Radio, Loader2, Wifi } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, changeLang } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      window.location.href = "/";
    } catch (err) {
      setError(apiErr(err.response?.data?.detail, t("login.error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden grain" style={{ background: "#0d121c" }}>
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,15,23,0.7), rgba(11,15,23,0.95))" }} />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-blue-600 flex items-center justify-center">
            <Radio className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-lg">GoWeb RADIUS Manager</div>
            <div className="text-xs uppercase tracking-widest text-blue-400">FreeRADIUS · MikroTik HotSpot</div>
          </div>
        </div>
        <div className="relative max-w-md">
          <Wifi className="h-10 w-10 text-blue-400 mb-4" />
          <h2 className="font-display text-3xl font-bold leading-tight">Un codice Wi-Fi univoco per ogni prenotazione</h2>
          <p className="text-slate-400 mt-3 text-sm leading-relaxed">
            Generazione automatica · Attivazione al check-in · Disabilitazione al check-out. Gestione centralizzata multi-struttura degli accessi Wi-Fi HotSpot.
          </p>
        </div>
        <div className="relative text-xs text-slate-500 font-mono">v1.0 · booking.goweb.srl ready</div>
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12 relative">
        <div className="absolute top-6 right-6 flex gap-1">
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`login-lang-${l.code}`}
              onClick={() => changeLang(l.code)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold font-mono transition-colors ${lang === l.code ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-200"}`}
            >
              {l.flag}
            </button>
          ))}
        </div>
        <div className="w-full max-w-sm mx-auto">
          <h1 className="font-display text-2xl font-bold">{t("login.title")}</h1>
          <p className="text-sm text-slate-400 mt-1 mb-8">{t("login.subtitle")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("login.email")}</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-lg bg-[#111827] border border-[#1F2937] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("login.password")}</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-lg bg-[#111827] border border-[#1F2937] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-rose-400 bg-rose-500/10 rounded-lg px-3 py-2 ring-1 ring-rose-500/20">{error}</div>}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 py-2.5 text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("login.submit")}
            </button>
          </form>
          <p className="text-xs text-slate-600 mt-6 font-mono">GoWeb RADIUS Manager · v1.0</p>
        </div>
      </div>
    </div>
  );
}
